import { isNil } from 'lodash-es';
import type { RenderStreamingVideoElement } from './index';

export const toNumber = (value: unknown) => {
  if (isNil(value) || value === '') return undefined;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

export const readNumberFromKeys = (value: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    const numberValue = toNumber(value[key]);
    if (!isNil(numberValue)) return numberValue;
  }
  return undefined;
};

export const readTuplePosition = (value: unknown): [number, number, number] | undefined => {
  if (!Array.isArray(value) || value.length < 3) return undefined;
  const position = value.slice(0, 3).map(item => toNumber(item));
  if (position.some(item => isNil(item))) return undefined;
  return position as [number, number, number];
};

export const notEmpty = <T>(item: T | null | undefined): item is T => !isNil(item);

type UnityPositionSource = {
  pointLng?: unknown;
  pointLat?: unknown;
  position?: unknown;
  unityPosition?: unknown;
  location?: unknown;
  coordinate?: unknown;
};

export const getUnityPosition = (
  source: UnityPositionSource,
  videoElement?: RenderStreamingVideoElement,
  logContext?: string
): [number, number, number] | undefined => {
  const sourceData = source as Record<string, any>;
  const tuplePosition =
    readTuplePosition(sourceData.position) ??
    readTuplePosition(sourceData.unityPosition) ??
    readTuplePosition(sourceData.location) ??
    readTuplePosition(sourceData.coordinate);

  if (tuplePosition) return tuplePosition;

  const x = readNumberFromKeys(sourceData, ['x', 'posX', 'positionX', 'unityX', 'coordX']);
  const y = readNumberFromKeys(sourceData, ['y', 'posY', 'positionY', 'unityY', 'coordY']);
  const z = readNumberFromKeys(sourceData, ['z', 'posZ', 'positionZ', 'unityZ', 'coordZ']);
  if (!isNil(x) && !isNil(y) && !isNil(z)) return [x, y, z];

  const lng = toNumber(source.pointLng);
  const lat = toNumber(source.pointLat);
  if (!isNil(lng) && !isNil(lat) && videoElement?.projectToWorld) {
    try {
      return videoElement.projectToWorld([lng, lat, 0]);
    } catch (error) {
      if (logContext) {
        console.warn(`${logContext} projectToWorld failed`, { source, error });
      }
    }
  }

  return undefined;
};
